/**
 * PATH: packages/ui/src/kanban/components/card-detail/entries/useEntriesAiFill.ts
 * FILE_ID: UI-KANBAN-ENTRIES-AIFILL-HOOK-001
 * PURPOSE: State + API call for the Entries table's "AI draft rows"
 *          button — one click asks the card-suggest reasoner for the
 *          standard next steps and lands them as EDITABLE draft rows.
 * WHY: Drafts are the ✓/✗ surface: × removes a row (✗), "Save as
 *      sub-cards" is the accept (✓). Nothing persists until Save, so
 *      the AI can never mint sub-cards on its own (same confirm-to-
 *      apply rule as LiveSuggestionStrip / AiActionsBar).
 * FLOW:
 *   click → buildEntriesAiContext(card, entries)
 *     → api.suggestCardActions → suggestionsToDrafts → setDrafts
 * RELATED:
 *   - entryAiFill.ts: the pure transforms (tested separately)
 *   - CardEntriesSection.tsx: renders the button + hint
 */

import { useCallback, useState } from 'react'
import type { KanbanApi, KanbanMember } from '../../../api'
import type { Card } from '../../../types'
import {
  buildEntriesAiContext,
  contextTooShort,
  suggestionsToDrafts,
} from './entryAiFill'
import { draftIsBlank, type EntryDraftRow } from './entryDraft'

export interface UseEntriesAiFillResult {
  /** Ask the reasoner and land the rows. No-op while already busy. */
  aiFill: () => Promise<void>
  aiBusy: boolean
  /** User-facing outcome line (why nothing landed), or null. */
  aiHint: string | null
}

export function useEntriesAiFill({
  card,
  api,
  entries,
  members,
  drafts,
  setDrafts,
}: {
  card: Card
  api: KanbanApi
  entries: Card[]
  members?: KanbanMember[]
  drafts: EntryDraftRow[]
  setDrafts: (rows: EntryDraftRow[]) => void
}): UseEntriesAiFillResult {
  const [aiBusy, setAiBusy] = useState(false)
  const [aiHint, setAiHint] = useState<string | null>(null)

  const aiFill = useCallback(async () => {
    if (!api.suggestCardActions || aiBusy) return
    const context = buildEntriesAiContext(card, entries)
    if (contextTooShort(context)) {
      setAiHint('Add a description to the card first so AI has context to draft from.')
      return
    }
    setAiBusy(true)
    setAiHint(null)
    try {
      const result = await api.suggestCardActions(card.id, context)
      if (!result.available) {
        setAiHint('AI is unavailable right now — add rows manually.')
        return
      }
      if (result.reason === 'cooldown') {
        setAiHint('AI is cooling down — try again in a few seconds.')
        return
      }
      const rows = suggestionsToDrafts(result.suggestions, members)
      if (rows.length === 0) {
        setAiHint('AI found nothing to add beyond what is already tabled.')
        return
      }
      // Same landing rule as paste-from-Excel: keep rows the operator
      // already touched, replace untouched blanks with the AI drafts.
      setDrafts([...drafts.filter((d) => !draftIsBlank(d)), ...rows])
      setAiHint(`${rows.length} draft row${rows.length === 1 ? '' : 's'} added — edit, remove, then "Save as sub-cards".`)
    } catch (err) {
      setAiHint(err instanceof Error ? err.message : 'AI draft failed.')
    } finally {
      setAiBusy(false)
    }
  }, [aiBusy, api, card, drafts, entries, members, setDrafts])

  return { aiFill, aiBusy, aiHint }
}
